import {Button, Dialog, DialogActions, DialogContent, Stack, Typography} from "@mui/material";
import {MyDialogTitle} from "@/components/dialog/MyDialogTitle.tsx";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {FormProvider, useForm} from "react-hook-form";
import {FormTextField} from "@/components/form/FormTextField.tsx";
import {useCallback} from "react";
import {RealEstateBrokerFragmentFragment, useCreateRealEstateBrokerMutation,} from "@/graphql/graphql";
import toast from "react-hot-toast";

interface CreateNewRealEstateBrokerDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateSuccess?: (value: RealEstateBrokerFragmentFragment) => Promise<void>;
}

const schema = z.object({
  licenseNumber: z.string().min(2),
  mainOfficeAddress: z.string().min(2),
  name: z.string().min(2),
  phoneNumber: z.string().min(2),
  representativeName: z.string().min(2),
})

export function CreateNewRealEstateBrokerDialog(
  {open, onClose, onCreateSuccess}: CreateNewRealEstateBrokerDialogProps
) {
  
  const create = useCreateRealEstateBrokerMutation()
  
  const form = useForm({
    defaultValues: {
      licenseNumber: '国土交通大臣',
      mainOfficeAddress: '',
      name: '',
      phoneNumber: '',
      representativeName: '',
    },
    resolver: zodResolver(schema),
    mode: "onChange"
  })
  
  const onSubmit = useCallback(async (
    value: z.infer<typeof schema>
  ) => {
    const toastId = toast.loading('作成中...')
    await create.mutateAsync({
      input: value
    }).then(
      (res) => {
        if (res.createRealEstateBroker) {
          toast.success('作成しました', {id: toastId})
          onClose()
          if (onCreateSuccess) {
            onCreateSuccess(res.createRealEstateBroker)
          }
        }
      }
    ).catch(
      () => {
        toast.error('作成に失敗しました', {id: toastId})
      }
    )
  }, [create, onClose, onCreateSuccess])
  
  return <Dialog open={open} onClose={onClose} maxWidth={'sm'} fullWidth>
    <MyDialogTitle
      onClose={onClose}
    >
      宅地建物取引業者 新規作成
    </MyDialogTitle>
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant={'formLabel'}>免許証番号</Typography>
            <FormTextField name={'licenseNumber'} control={form.control} placeholder={'国土交通大臣(1)第12345号'}/>
            <Typography variant={'formLabel'}>主たる事務所の所在地</Typography>
            <FormTextField name={'mainOfficeAddress'} control={form.control} placeholder={"東京都渋谷区渋谷1-1-1"}/>
            <Typography variant={'formLabel'}>商号または名称</Typography>
            <FormTextField name={'name'} control={form.control} placeholder={'株式会社不動産'}/>
            <Typography variant={'formLabel'}>電話番号</Typography>
            <FormTextField name={'phoneNumber'} control={form.control} placeholder={'03-1234-5678'}/>
            <Typography variant={'formLabel'}>代表者氏名</Typography>
            <FormTextField name={'representativeName'} control={form.control} placeholder={'不動産太郎'}/>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>キャンセル</Button>
          <Button type={'submit'} variant={'contained'} disabled={!form.formState.isValid}>作成</Button>
        </DialogActions>
      </form>
    </FormProvider>
  
  </Dialog>
}
